import React, { useEffect, useMemo, useRef, useState, useId } from "react";

/**
 * Лёгкая бесконечная горизонтальная карусель для мобильных.
 * Дублирует контент (х2) и крутит бесшовно.
 *
 * @param {object} props
 * @param {'left'|'right'} [props.direction='left']
 * @param {number} [props.speed=0.4] // px per frame
 * @param {React.ReactNode} props.children
 */
const MobileInfiniteRow = ({ children, direction = "left", speed = 0.4 }) => {
    const trackRef = useRef(null);
    const [offset, setOffset] = useState(0);
    const [half, setHalf] = useState(0);

    // уникальный id экземпляра, чтобы keys не пересекались у разных рядов
    const uid = useId();

    const itemsArray = useMemo(() => React.Children.toArray(children), [children]);

    // Делаем два набора и ПЕРЕОПРЕДЕЛЯЕМ key у каждого элемента,
    // чтобы не было дубликатов (.$0, .$1 и т.п.)
    const duplicated = useMemo(() => {
        const first = itemsArray.map((child, idx) =>
            React.isValidElement(child)
                ? React.cloneElement(child, { key: `${uid}-a-${idx}` })
                : <span key={`${uid}-a-${idx}`}>{child}</span>
        );
        const second = itemsArray.map((child, idx) =>
            React.isValidElement(child)
                ? React.cloneElement(child, { key: `${uid}-b-${idx}` })
                : <span key={`${uid}-b-${idx}`}>{child}</span>
        );
        return [...first, ...second];
    }, [itemsArray, uid]);

    // Измеряем ширину одного набора (половину общего scrollWidth)
    useEffect(() => {
        if (!trackRef.current) return;

        const el = trackRef.current;

        const measure = () => {
            const total = el.scrollWidth || 0;
            const h = total / 2;
            setHalf(h);
            // для right начинаем с -half, для left — с 0
            setOffset(direction === "right" ? -h : 0);
        };

        // измеряем после рендера
        const raf = requestAnimationFrame(measure);

        // Следим за изменениями размеров
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [duplicated.length, direction]);

    // Анимация (бесшовная обёртка)
    useEffect(() => {
        if (!half) return;
        let rafId;

        const animate = () => {
            setOffset(prev => {
                const delta = direction === "left" ? -speed : speed;
                const next = prev + delta;

                if (direction === "left" && next <= -half) return 0;     // 0 → -half → 0
                if (direction === "right" && next >= 0) return -half;    // -half → 0 → -half

                return next;
            });

            rafId = requestAnimationFrame(animate);
        };

        rafId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafId);
    }, [half, direction, speed]);

    return (
        <div
            style={{
                overflow: "hidden",
                width: "100%",
                position: "relative"
            }}
        >
            <div
                ref={trackRef}
                style={{
                    display: "flex",
                    flexWrap: "nowrap",
                    transform: `translateX(${offset}px)`,
                    willChange: "transform",
                    gap: 10
                }}
            >
                {duplicated}
            </div>
        </div>
    );
};

export default MobileInfiniteRow;
